import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { 
  formatCurrency, 
  formatDate, 
  getStatusColor, 
  getPriorityColor,
  generateOrderTimeline,
  calculateOrderProgress 
} from './liveOrdersUtils';

// Icon components (you'll need to install react-icons or use your preferred icon library)
import {
  XMarkIcon,
  UserIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon as CancelIcon,
  PrinterIcon,
  ArrowDownTrayIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  BellIcon,
  EllipsisHorizontalCircleIcon,
  PaperAirplaneIcon  
} from '@heroicons/react/24/outline';

// Move this OUTSIDE the OrderDetailsModal component
const SMSDialog = ({ 
  smsDialog, 
  setSmsDialog, 
  smsData, 
  setSmsData, 
  handleSendSMS,
  currentRecipient 
}) => (
  smsDialog && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center">
            <ChatBubbleLeftRightIcon className="w-6 h-6 text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Send SMS</h3>
          </div>
          <button
            onClick={() => setSmsDialog(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={smsData.to}
              onChange={(e) => setSmsData({ ...smsData, to: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter phone number"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message
            </label>
            <textarea
              value={smsData.message}
              onChange={(e) => setSmsData({ ...smsData, message: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Type your SMS message here..."
              maxLength={160}
            />
            <p className="text-xs text-gray-500 mt-1">
              {smsData.message.length}/160 characters
            </p>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setSmsDialog(false)}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSendSMS}
            disabled={!smsData.to || !smsData.message}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            <PaperAirplaneIcon className="w-4 h-4" />
            <span>Send SMS</span>
          </button>
        </div>
      </div>
    </div>
  )
);

// Move this OUTSIDE the OrderDetailsModal component (along with SMSDialog)
const WhatsAppDialog = ({ 
  whatsappDialog, 
  setWhatsappDialog, 
  whatsappData, 
  setWhatsappData, 
  handleSendWhatsApp,
  currentRecipient 
}) => (
  whatsappDialog && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center">
            <svg className="w-6 h-6 text-green-600 mr-2" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
            </svg>
            <h3 className="text-lg font-semibold text-gray-900">Send WhatsApp Message</h3>
          </div>
          <button
            onClick={() => setWhatsappDialog(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={whatsappData.to}
              onChange={(e) => setWhatsappData({ ...whatsappData, to: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Enter phone number"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message
            </label>
            <textarea
              value={whatsappData.message}
              onChange={(e) => setWhatsappData({ ...whatsappData, message: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Type your WhatsApp message here..."
            />
            <p className="text-xs text-gray-500 mt-1">
              {whatsappData.message.length} characters
            </p>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setWhatsappDialog(false)}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSendWhatsApp}
            disabled={!whatsappData.to || !whatsappData.message}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            <PaperAirplaneIcon className="w-4 h-4" />
            <span>Send WhatsApp</span>
          </button>
        </div>
      </div>
    </div>
  )
);

const OrderDetailsModal = ({ 
  open, 
  onClose, 
  order, 
  onStatusUpdate, 
  onSendEmail, 
  onUpdateOrder 
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [editedOrder, setEditedOrder] = useState(null);
  const [emailDialog, setEmailDialog] = useState(false);

  const URL = process.env.REACT_APP_FORE_NOTIFY;

  // Add these new state variables for mobile actions
  const [mobileActionsVisible, setMobileActionsVisible] = useState(false);
  const [activePopupId, setActivePopupId] = useState(null); // Add this to track which popup is active
  const [smsDialog, setSmsDialog] = useState(false);
  const [whatsappDialog, setWhatsappDialog] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState('');
  const [waitingTimeLeft, setWaitingTimeLeft] = useState(null);
  const [supplierWaitingTimes, setSupplierWaitingTimes] = useState({});
  const [customerWaitingTime, setCustomerWaitingTime] = useState(null);
  // Add loading states to your component state
  const [smsLoading, setSmsLoading] = useState(false);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  // Add recipient tracking state
  const [currentRecipient, setCurrentRecipient] = useState(null);

  //console.log("waitingTimeLeft", waitingTimeLeft);
  //console.log("order.order_waiting_time", order?.order_waiting_time);
  //console.log("order.createdAt", order?.created_at);

  const [emailData, setEmailData] = useState({
    to: '',
    subject: '',
    message: ''
  });

    // Add these new state variables for SMS and WhatsApp
  const [smsData, setSmsData] = useState({
    to: '',
    message: ''
  });

  const [whatsappData, setWhatsappData] = useState({
    to: '',
    message: ''
  });

  useEffect(() => {
    if (order) {
      setEditedOrder({ ...order });
/*       setEmailData({
        to: order.customerEmail,
        subject: `Regarding Order ${order.id}`,
        message: ''
      }); */
    }
  }, [order]);
  
// Add click outside handler
useEffect(() => {
  const handleClickOutside = (event) => {
    // Only handle click outside when mobileActionsVisible is true
    if (mobileActionsVisible && !event.target.closest('.mobile-actions-container')) {
      setMobileActionsVisible(false);
      setActivePopupId(null);
      setSelectedPhoneNumber('');
    }
  };

  // Only add event listener when mobileActionsVisible is true
  if (mobileActionsVisible) {
    document.addEventListener('mousedown', handleClickOutside);
  }

  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, [mobileActionsVisible]);


  useEffect(() => {
    if (order?.created_at && order?.order_waiting_time) {
      const createdAt = new Date(order.created_at);
      const waitingTimeMinutes = order.order_waiting_time;
      //console.log("createdAt", createdAt);
      //console.log("waitingTimeMinutes", waitingTimeMinutes);

      const updateTimer = () => {
        const now = new Date();
        const elapsedSeconds = Math.floor((now - createdAt) / 1000);
        const totalWaitingSeconds = waitingTimeMinutes * 60;
        const remainingSeconds = Math.max(0, totalWaitingSeconds - elapsedSeconds);
        
        if (remainingSeconds > 0) {
          const minutes = Math.floor(remainingSeconds / 60);
          const seconds = remainingSeconds % 60;
          setWaitingTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        } else {
          setWaitingTimeLeft('0:00');
        }
      };
      
      updateTimer();
      const interval = setInterval(updateTimer, 1000); 
      
      return () => clearInterval(interval);
    }
  }, [order?.created_at, order?.order_waiting_time]);

// Update the useEffect for supplier waiting times
useEffect(() => {
  if (order?.suppliers && order?.order_waiting_time && order?.created_at) {
    const updateSupplierTimers = () => {
      const newSupplierWaitingTimes = {};
      
      order.suppliers.forEach((supplier, index) => {
        // More robust supplier ID extraction
        const supplierId = supplier._id || supplier.vendor_id || supplier.id;
        if (!supplierId) {
          console.warn('No supplier ID found for supplier:', supplier);
          return;
        }
        
        // More robust check for supplier response
        const hasResponded = order?.vendorResponses?.some(response => {
          const responseVendorId = response.vendor_id?._id || response.vendor_id?.id || response.vendor_id;
          const supplierIdStr = supplierId.toString();
          const responseIdStr = responseVendorId ? responseVendorId.toString() : null;
          
          const idsMatch = responseIdStr && responseIdStr === supplierIdStr;
          const actuallyResponded = response.status && response.status.toLowerCase() !== 'respond now';
          
          return idsMatch && actuallyResponded;
        });

        
        //console.log(`Supplier ${supplierId} has responded:`, hasResponded);
        
        // Always create timer data for suppliers (whether they responded or not for debugging)
        // Remove the hasResponded check temporarily to see all suppliers
        const createdAt = new Date(order.created_at);
        const waitingTimeMinutes = parseInt(order.order_waiting_time);
        const now = new Date();
        
        // Add validation for valid dates
        if (isNaN(createdAt.getTime()) || isNaN(waitingTimeMinutes)) {
          console.warn('Invalid date or waiting time for supplier:', supplierId);
          return;
        }
        
        const elapsedSeconds = Math.floor((now - createdAt) / 1000);
        const totalWaitingSeconds = waitingTimeMinutes * 60;
        const remainingSeconds = Math.max(0, totalWaitingSeconds - elapsedSeconds);
        
        if (remainingSeconds > 0) {
          const minutes = Math.floor(remainingSeconds / 60);
          const seconds = remainingSeconds % 60;
          newSupplierWaitingTimes[supplierId] = {
            timeLeft: `${minutes}:${seconds.toString().padStart(2, '0')}`,
            remainingSeconds,
            totalSeconds: totalWaitingSeconds,
            hasResponded // Add this for debugging
          };
        } else {
          newSupplierWaitingTimes[supplierId] = {
            timeLeft: '0:00',
            remainingSeconds: 0,
            totalSeconds: totalWaitingSeconds,
            hasResponded // Add this for debugging
          };
        }
        
        //console.log(`Timer data for supplier ${supplierId}:`, newSupplierWaitingTimes[supplierId]);
      });
      
      //console.log('All supplier waiting times:', newSupplierWaitingTimes);
      setSupplierWaitingTimes(newSupplierWaitingTimes);
    };
    
    updateSupplierTimers();
    const interval = setInterval(updateSupplierTimers, 1000);
    
    return () => clearInterval(interval);
  } else {
/*     console.log('Missing required data for timers:', {
      suppliers: !!order?.suppliers,
      waiting_time: !!order?.order_waiting_time,
      created_at: !!order?.created_at
    }); */
    setSupplierWaitingTimes({});
  }
}, [order?.suppliers, order?.order_waiting_time, order?.created_at, order?.vendorResponses]);




// Function to get waiting time color
const getWaitingTimeColor = (remainingSeconds, totalSeconds) => {
  if (!remainingSeconds || !totalSeconds || totalSeconds === 0) {
    return 'text-gray-600 bg-gray-100';
  }
  
  const percentage = (remainingSeconds / totalSeconds) * 100;
  
  if (percentage > 50) {
    return 'text-green-600 bg-green-100';
  } else if (percentage > 25) {
    return 'text-orange-600 bg-orange-100';
  } else {
    return 'text-red-600 bg-red-100';
  }
};



// Add this useEffect for detailed debugging
useEffect(() => {
  if (order?.suppliers) {
    //console.log('=== SUPPLIER DEBUG INFO ===');
    order.suppliers.forEach((supplier, index) => {
/*       console.log(`Supplier ${index}:`, {
        _id: supplier._id,
        vendor_id: supplier.vendor_id,
        id: supplier.id,
        name: supplier.name,
        fullObject: supplier
      }); */
    });
    
    if (order?.vendorResponses) {
      //console.log('=== VENDOR RESPONSES DEBUG INFO ===');
      order.vendorResponses.forEach((response, index) => {
       /*  console.log(`Response ${index}:`, {
          vendor_id: response.vendor_id,
          vendor_id_id: response.vendor_id?._id,
          fullObject: response
        }); */
      });
    }
    
    //console.log('=== TIMER STATE ===');
    //console.log('supplierWaitingTimes keys:', Object.keys(supplierWaitingTimes));
    //console.log('supplierWaitingTimes:', supplierWaitingTimes);
  }
}, [order, supplierWaitingTimes]);


// Add useEffect for customer waiting time
useEffect(() => {
  if (order?.order_waiting_time && order?.created_at) {
    const updateCustomerTimer = () => {
      const createdAt = new Date(order.created_at);
      const waitingTimeMinutes = parseInt(order.order_waiting_time);
      const now = new Date();
      
      // Add validation for valid dates
      if (isNaN(createdAt.getTime()) || isNaN(waitingTimeMinutes)) {
        console.warn('Invalid date or waiting time for customer');
        return;
      }
      
      const elapsedSeconds = Math.floor((now - createdAt) / 1000);
      const totalWaitingSeconds = waitingTimeMinutes * 60;
      const remainingSeconds = Math.max(0, totalWaitingSeconds - elapsedSeconds);
      
      if (remainingSeconds > 0) {
        const minutes = Math.floor(remainingSeconds / 60);
        const seconds = remainingSeconds % 60;
        setCustomerWaitingTime({
          timeLeft: `${minutes}:${seconds.toString().padStart(2, '0')}`,
          remainingSeconds,
          totalSeconds: totalWaitingSeconds
        });
      } else {
        setCustomerWaitingTime({
          timeLeft: '0:00',
          remainingSeconds: 0,
          totalSeconds: totalWaitingSeconds
        });
      }
    };
    
    updateCustomerTimer();
    const interval = setInterval(updateCustomerTimer, 1000);
    
    return () => clearInterval(interval);
  } else {
    setCustomerWaitingTime(null);
  }
}, [order?.order_waiting_time, order?.created_at]);

// Add this to your existing debug useEffect or create a new one
useEffect(() => {
  //console.log('Customer waiting time:', customerWaitingTime);
/*   console.log('Customer timer data:', {
    order_waiting_time: order?.order_waiting_time,
    created_at: order?.created_at
  }); */
}, [customerWaitingTime, order?.order_waiting_time, order?.created_at]);


  if (!order || !open) return null;


  // Add additional validation
const hasValidTimerData = order?.suppliers && order?.order_waiting_time && order?.created_at;
//console.log('Has valid timer data:', hasValidTimerData);

  const timeline = generateOrderTimeline(order);
  const progress = calculateOrderProgress(order);

  // Add these handler functions
  const handlePhoneClick = (phoneNumber, popupId) => {
    if (activePopupId === popupId && mobileActionsVisible) {
      // If clicking on the same popup that's already open, close it
      setMobileActionsVisible(false);
      setActivePopupId(null);
      setSelectedPhoneNumber('');
    } else {
      // Open the clicked popup and close others
      setSelectedPhoneNumber(phoneNumber);
      setActivePopupId(popupId);
      setMobileActionsVisible(true);
    }
  };

   const handlePhoneCall = (phoneNumber) => {
    if (phoneNumber) {
      window.location.href = `tel:${phoneNumber}`;
    }
    setMobileActionsVisible(false);
    setActivePopupId(null);
    setSelectedPhoneNumber('');
  };


  const handleSMSClick = (phoneNumber,recipientInfo = null) => {
    setSmsData({ to: phoneNumber, message: '' });
    
    // Store recipient information
    setCurrentRecipient(recipientInfo);

    setSmsDialog(true);
    setMobileActionsVisible(false);
    setActivePopupId(null);
    setSelectedPhoneNumber('');
  };

  const handleWhatsAppClick = (phoneNumber,recipientInfo = null) => {
    setWhatsappData({ to: phoneNumber, message: '' });

    // Store recipient information
    setCurrentRecipient(recipientInfo);

    setWhatsappDialog(true);
    setMobileActionsVisible(false);
    setActivePopupId(null);
    setSelectedPhoneNumber('');
  };


  // Add email click handler
const handleEmailClick = (email, recipientInfo = null) => {
  setEmailData({ 
    to: email || '', 
    subject: `Regarding Order ${order.id}`, 
    message: '' 
  });
  
  // Store recipient information
  setCurrentRecipient(recipientInfo);
  
  setEmailDialog(true);
  setMobileActionsVisible(false);
  setActivePopupId(null);
  setSelectedPhoneNumber('');
};

/*   const handleSendSMS = () => {
    console.log('Sending SMS:', smsData);
    // Add your SMS sending logic here
    setSmsDialog(false);
  };

  const handleSendWhatsApp = () => {
    console.log('Sending WhatsApp:', whatsappData);
    // Add your WhatsApp sending logic here
    setWhatsappDialog(false);
  }; */


const handleSendSMS = async () => {
  try {
    setSmsLoading(true);
    console.log('Sending SMS:', smsData);
    console.log('Current recipient:', currentRecipient);

    // Validate required fields
    if (!smsData.to || !smsData.message) {
      alert('Please fill in all required fields');
      return;
    }

    // Validate message length
    if (smsData.message.length > 160) {
      alert('SMS message cannot exceed 160 characters');
      return;
    }

    let to = smsData.to.trim();

    // Remove any existing +91 prefix to avoid duplication
    to = to.replace(/^\+91/, '');
    
    // Remove any non-digit characters except +
    to = to.replace(/[^\d]/g, '');
    
    // Add +91 prefix if number is provided
    if (to) {
      to = '+91' + to;
    }

    // Prepare the request payload
    const payload = {
      recipientId: currentRecipient?.recipientId,
      recipientRole: currentRecipient?.recipientRole,
      to: to,
      message: smsData.message,
      app: 'brickbucket',
      entity: 'Order',
      entityId: order?.id || order?._id
    };

    console.log('SMS API payload:', payload);

    // Make API call
    const response = await fetch(URL + 'notifications/send-sms', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    const result = await response.json();

    if (response.ok && result.success) {
      console.log('SMS sent successfully:', result);
      
      // Show success message (you can replace with a toast notification)
      alert(`SMS sent successfully! ${to || 'N/A'}`);
      
      // Close dialog and reset form
      setSmsDialog(false);
      setSmsData({ to: '', message: '' });
      setCurrentRecipient(null); // Reset recipient info

    } else {
      console.error('SMS sending failed:', result);
      alert(result.message || 'Failed to send SMS');
    }

  } catch (error) {
    console.error('Error sending SMS:', error);
    alert('Network error. Please check your connection and try again.');
  } finally {
    setSmsLoading(false);
  }
};

const handleSendWhatsApp = async () => {
  try {
    setWhatsappLoading(true);
    console.log('Sending WhatsApp:', whatsappData);
    console.log('Current recipient:', currentRecipient);

    // Validate required fields
    if (!whatsappData.to || !whatsappData.message) {
      alert('Please fill in all required fields');
      return;
    }

    let to = whatsappData.to.trim();

    // Remove any existing +91 prefix to avoid duplication
    to = to.replace(/^\+91/, '');
    
    // Remove any non-digit characters except +
    to = to.replace(/[^\d]/g, '');
    
    // Add +91 prefix if number is provided
    if (to) {
      to = '+91' + to;
    }

    // Prepare the request payload
    const payload = {
      recipientId: currentRecipient?.recipientId,
      recipientRole: currentRecipient?.recipientRole,
      to: to,
      message: whatsappData.message,
      app: 'brickbucket',
      entity: 'Order',
      entityId: order?.id || order?._id
    };

    console.log('WhatsApp API payload:', payload);

    // Make API call
    const response = await fetch(URL + 'notifications/send-whatsapp', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    const result = await response.json();

    if (response.ok && result.success) {
      console.log('WhatsApp sent successfully:', result);
      
      // Show success message
      alert(`WhatsApp message sent successfully! Message ID: ${to || 'N/A'}`);
      
      // Close dialog and reset form
      setWhatsappDialog(false);
      setWhatsappData({ to: '', message: '' });
      setCurrentRecipient(null); // Reset recipient info

    } else {
      console.error('WhatsApp sending failed:', result);
      alert(result.message || 'Failed to send WhatsApp message');
    }

  } catch (error) {
    console.error('Error sending WhatsApp:', error);
    alert('Network error. Please check your connection and try again.');
  } finally {
    setWhatsappLoading(false);
  }
};



   // Helper function to get notifications for a specific recipient
  const getNotificationsForRecipient = (recipientId) => {
    if (!order.notifications || !Array.isArray(order.notifications)) {
      return [];
    }
    
    return order.notifications.filter(notification => 
      notification.recipientId && notification.recipientId.toString() === recipientId.toString()
    );
  };

  // Helper function to get customer notifications
  const getCustomerNotifications = () => {
    if (!order.rawQuote || !order.rawQuote.customer_id) return [];
    const customerId = order.rawQuote.customer_id._id || order.rawQuote.customer_id;
    return getNotificationsForRecipient(customerId);
  };

  // Helper function to get supplier notifications
/*   const getSupplierNotifications = () => {
    if (!order.suppliers || order.suppliers.length === 0) return [];
    const supplierId = order.suppliers[0]?._id || order.suppliers[0]._id;
    if (!supplierId) return [];
    return getNotificationsForRecipient(supplierId);
  }; */

  // Update the helper function to get all supplier notifications
const getAllSupplierNotifications = () => {
  if (!order.suppliers || order.suppliers.length === 0) return [];
  
  let allSupplierNotifications = [];
  
  order.suppliers.forEach(supplier => {
    const supplierId = supplier?._id || supplier.vendor_id;
    if (supplierId) {
      const supplierNotifications = getNotificationsForRecipient(supplierId);
      if (supplierNotifications.length > 0) {
        allSupplierNotifications.push({
          supplierId: supplierId,
          supplierName: supplier?.name || 'Unknown Supplier',
          supplierEmail: supplier?.email,
          supplierPhone: supplier?.mobile_number,
          supplierAddress: supplier?.address,
          notifications: supplierNotifications
        });
      }
    }
  });
  
  return allSupplierNotifications;
};


  // Component to render notifications
  const NotificationsList = ({ notifications, title }) => {
    if (!notifications || notifications.length === 0) {
      return null;
    }

    return (
      <div className="mt-4 p-3 ">{/* bg-blue-50 border border-blue-200 rounded-md */}
{/*         <div className="flex items-center mb-2">
          <BellIcon className="w-4 h-4 text-blue-500 mr-2" />
          <h4 className="text-sm font-medium text-blue-800">{title}</h4>
          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {notifications.length}
          </span>
        </div> */}
        <div className="space-y-2 max-h-32 overflow-y-auto">
          {notifications.map((notification, index) => (
            <div key={index} className="text-xs text-blue-700 bg-white p-2 rounded border border-blue-100">
              <div className="flex justify-between items-start">
                <div className="flex-1">
{/*                   <p className="font-medium">{notification.title || notification.why}</p>
                  <p className="text-blue-600 mt-1">{notification.message || notification.what}</p> */}
                  <p className="text-blue-500 mt-1">{notification.metadata.order_status}</p>
                </div>
                <div className="ml-2 flex-shrink-0">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                    notification.status === 'sent' ? 'bg-green-100 text-green-800' :
                    notification.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {notification.status || 'pending'}
                  </span>
                </div>
              </div>
              <p className="text-blue-500 mt-1">
                {formatDate(notification.when || notification.createdAt, 'datetime')}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

    // Get notifications at component level
  const customerNotifications = getCustomerNotifications();
  //const supplierNotifications = getSupplierNotifications();
  const allSupplierNotifications = getAllSupplierNotifications();
  const totalSupplierNotifications = allSupplierNotifications.reduce((total, supplier) => total + supplier.notifications.length, 0);

// Add this function at the component level (after your other helper functions)
const calculateVendorResponseTotal = (vendorResponses, supplier) => {
  //console.log('Calculating total for supplier:', supplier);
  //console.log('Available vendor responses:', vendorResponses);
  
  if (!Array.isArray(vendorResponses) || !supplier || !supplier._id) {
    return 0;
  }
  
  // Find the vendor response that matches the supplier ID
  const matchingVendorResponse = vendorResponses.find(vendorResponse => 
    vendorResponse.vendor_id?._id && 
    vendorResponse.vendor_id._id.toString() === supplier._id.toString()
  );
  
  if (!matchingVendorResponse) {
    //console.log('No matching vendor response found for supplier:', supplier._id);
    return 0;
  }
  
  //console.log('Found matching vendor response:', matchingVendorResponse);
  
  if (!matchingVendorResponse.items || !Array.isArray(matchingVendorResponse.items)) {
    return 0;
  }
  
  // Calculate total from the matching vendor response items
  const total = matchingVendorResponse.items.reduce((total, item) => {
    const itemPrice = item.quotation_price || item.proposed_price || item.price || 0;
    const quantity = item.quantity || 1;
    return total + (itemPrice * quantity) + (itemPrice * 0.18); // Assuming 18% tax
  }, 0);
  
  //console.log('Calculated total for supplier', supplier._id, ':', total);
  return total;
};




  const handleTabChange = (newValue) => {
    setActiveTab(newValue);
  };

  const handleStatusChange = (newStatus) => {
    onStatusUpdate(order.id, newStatus);
  };

  const handleSaveEdit = () => {
    onUpdateOrder(editedOrder);
    setEditMode(false);
  };

  const handleCancelEdit = () => {
    setEditedOrder({ ...order });
    setEditMode(false);
  };

/*   const handleSendEmail = () => {
    onSendEmail(order.id, emailData);
    setEmailDialog(false);
  }; */


  const handleSendEmail = async () => {
  try {
    setEmailLoading(true);
    console.log('Sending Email:', emailData);
    console.log('Current recipient:', currentRecipient);
    
    // Validate required fields
    if (!emailData.to || !emailData.subject || !emailData.message) {
      alert('Please fill in all required fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailData.to)) {
      alert('Please enter a valid email address');
      return;
    }

    // Prepare the request payload
    const payload = {
      recipientId: currentRecipient?.recipientId,
      recipientRole: currentRecipient?.recipientRole || 'customer',
      to: emailData.to.trim(),
      subject: emailData.subject.trim(),
      message: emailData.message.trim(),
      app: 'brickbucket',
      entity: 'Order',
      entityId: order?.id || order?._id,
      type: 'plain', // or 'html' if you want to send HTML emails
      metadata: {
        recipientName: currentRecipient?.recipientName,
        recipientType: currentRecipient?.recipientType,
        supplierIndex: currentRecipient?.supplierIndex,
        orderId: order?.id || order?._id
      }
    };

    console.log('Email API payload:', payload);

    // Make API call
    const response = await fetch(URL + 'notifications/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Add authorization header if needed
        // 'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    const result = await response.json();

    if (response.ok && result.success) {
      console.log('Email sent successfully:', result);
      
      // Show success message with recipient info
      const recipientName = emailData.to;
      alert(`Email sent successfully to ${recipientName}!`);
      
      // Close dialog and reset form
      setEmailDialog(false);
      setEmailData({ to: '', subject: '', message: '' });
      setCurrentRecipient(null); // Reset recipient info
      
      // Optional: Refresh order data or update UI
      // if (onRefreshOrder) onRefreshOrder();
      
    } else {
      console.error('Email sending failed:', result);
      alert(result.message || 'Failed to send email');
    }

  } catch (error) {
    console.error('Error sending email:', error);
    alert('Network error. Please check your connection and try again.');
  } finally {
    setEmailLoading(false);
  }
};


  // Update the phone section in customer information
const renderCustomerPhone = () => {
    const popupId = 'customer-phone';
    const phoneNumber = order.customers[0].mobile_number;

    // Prepare customer recipient info
  const customerRecipientInfo = {
    recipientId: order.customers[0].customer_id || order?.rawQuote?.customer_id?._id,
    recipientRole: order.customers[0].roleId,
  };
    
    return (
      <div className="flex items-center relative mobile-actions-container">
        <PhoneIcon className="w-4 h-4 text-gray-400 mr-2" />
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700">Phone</label>
          <p 
            className="mt-1 text-sm text-blue-900 cursor-pointer hover:text-blue-600 transition-colors"
            onClick={() => handlePhoneClick(phoneNumber, popupId)}
          >
            {phoneNumber || 'Not provided'}
          </p>
          
          {/* Mobile Actions Dropdown - only show for this specific popup */}
          {mobileActionsVisible && activePopupId === popupId && selectedPhoneNumber && (
            <div className="absolute bottom-full left-0 mb-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
              <div className="py-2">
                <button
                  onClick={() => handlePhoneCall(selectedPhoneNumber)}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <PhoneIcon className="w-4 h-4 mr-3 text-green-500" />
                  Call
                </button>
                <button
                  onClick={() => handleSMSClick(selectedPhoneNumber,customerRecipientInfo)}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <ChatBubbleLeftRightIcon className="w-4 h-4 mr-3 text-blue-500" />
                  Send SMS
                </button>
                <button
                  onClick={() => handleWhatsAppClick(selectedPhoneNumber,customerRecipientInfo)}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-4 h-4 mr-3 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                  WhatsApp
                </button>
                <button
                  onClick={() => handleEmailClick(
                    order.customers[0].email, // or supplier.email for suppliers
                    customerRecipientInfo // or supplierRecipientInfo for suppliers
                  )}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <EnvelopeIcon className="w-4 h-4 mr-3 text-purple-500" />
                  Send Email
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };


// Update the renderSupplierPhone function
const renderSupplierPhone = (supplier, supplierIndex) => {
  const popupId = `supplier-phone-${supplierIndex}`;
  const phoneNumber = supplier?.mobile_number;

  // Prepare supplier recipient info
  const supplierRecipientInfo = {
    recipientId: supplier?._id || supplier?.vendor_id,
    recipientRole: supplier?.roleId,
    recipientName: supplier?.name || supplier?.company_name,
    supplierIndex: supplierIndex
  };

    // Get supplier email with fallback
  const supplierEmail = supplier?.email || supplier?.vendor_email || '';
  
  return (
    <div className="flex items-center relative mobile-actions-container">
      <PhoneIcon className="w-4 h-4 text-gray-400 mr-2" />
      <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700">Phone</label>
        <p 
          className="mt-1 text-sm text-blue-900 cursor-pointer hover:text-blue-600 transition-colors"
          onClick={() => handlePhoneClick(phoneNumber, popupId)}
        >
          {phoneNumber || 'Not provided'}
        </p>
        
        {/* Mobile Actions Dropdown - Updated positioning */}
        {mobileActionsVisible && activePopupId === popupId && selectedPhoneNumber && (
          <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
            <div className="py-2">
              <button
                onClick={() => handlePhoneCall(selectedPhoneNumber)}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <PhoneIcon className="w-4 h-4 mr-3 text-green-500" />
                Call
              </button>
              <button
                onClick={() => handleSMSClick(selectedPhoneNumber,supplierRecipientInfo)}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <ChatBubbleLeftRightIcon className="w-4 h-4 mr-3 text-blue-500" />
                Send SMS
              </button>
              <button
                onClick={() => handleWhatsAppClick(selectedPhoneNumber,supplierRecipientInfo)}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <svg className="w-4 h-4 mr-3 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                WhatsApp
              </button>
              <button
                onClick={() => handleEmailClick(
                  supplierEmail, // or supplier.email for suppliers
                  supplierRecipientInfo // or supplierRecipientInfo for suppliers
                )}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <EnvelopeIcon className="w-4 h-4 mr-3 text-purple-500" />
                Send Email
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};



  // Add SMS Dialog Component
/*   const SMSDialog = () => (
    smsDialog && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center">
              <ChatBubbleLeftRightIcon className="w-6 h-6 text-blue-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Send SMS</h3>
            </div>
            <button
              onClick={() => setSmsDialog(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                value={smsData.to}
                onChange={(e) => setSmsData({ ...smsData, to: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter phone number"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message
              </label>
              <textarea
                value={smsData.message}
                onChange={(e) => setSmsData({ ...smsData, message: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Type your SMS message here..."
                maxLength={160}
              />
                            <p className="text-xs text-gray-500 mt-1">
                {smsData.message.length}/160 characters
              </p>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setSmsDialog(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSendSMS}
              disabled={!smsData.to || !smsData.message}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              <PaperAirplaneIcon className="w-4 h-4" />
              <span>Send SMS</span>
            </button>
          </div>
        </div>
      </div>
    )
  ); */

  // Add WhatsApp Dialog Component
/*   const WhatsAppDialog = () => (
    whatsappDialog && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center">
              <svg className="w-6 h-6 text-green-600 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
              </svg>
              <h3 className="text-lg font-semibold text-gray-900">Send WhatsApp Message</h3>
            </div>
            <button
              onClick={() => setWhatsappDialog(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                value={whatsappData.to}
                onChange={(e) => setWhatsappData({ ...whatsappData, to: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Enter phone number"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message
              </label>
              <textarea
                value={whatsappData.message}
                onChange={(e) => setWhatsappData({ ...whatsappData, message: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Type your WhatsApp message here..."
              />
              <p className="text-xs text-gray-500 mt-1">
                {whatsappData.message.length} characters
              </p>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setWhatsappDialog(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSendWhatsApp}
              disabled={!whatsappData.to || !whatsappData.message}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              <PaperAirplaneIcon className="w-4 h-4" />
              <span>Send WhatsApp</span>
            </button>
          </div>
        </div>
      </div>
    )
  ); */


  const getStatusColorClass = (status) => {
    const colors = {
      pending: 'bg-yellow-500',
      confirmed: 'bg-blue-500',
      quotation_sent: 'bg-purple-500',
      awaiting_payment: 'bg-orange-500',
      processing: 'bg-indigo-500',
      shipped: 'bg-cyan-500',
      delivered: 'bg-green-500',
      completed: 'bg-emerald-500',
      on_hold: 'bg-gray-500',
      cancelled: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getPriorityColorClass = (priority) => {
    const colors = {
      low: 'bg-green-500',
      medium: 'bg-yellow-500',
      high: 'bg-orange-500',
      urgent: 'bg-red-500'
    };
    return colors[priority] || 'bg-gray-500';
  };

  const StatusChip = ({ status, label }) => (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white ${getStatusColorClass(status)}`}>
      {label}
    </span>
  );

  const PriorityChip = ({ priority, label }) => (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white ${getPriorityColorClass(priority)}`}>
      {label}
    </span>
  );

  const renderHeader = () => (
    <div className="flex justify-between items-center p-6 border-b border-gray-200">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          Order Details - #{order.id}
        </h2>
        <div className="flex gap-2 mt-2">
          <StatusChip
            status={order.status}
            label={order.status?.replace('_', ' ').toUpperCase()}
          />
{/*           <PriorityChip
            priority={order.priority}
            label={order.priority?.toUpperCase()}
          /> */}
        </div>
      </div>
      <div className="flex gap-2">
{/*         <button
          onClick={() => window.print()}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title="Print Order"
        >
          <PrinterIcon className="w-5 h-5" />
        </button>
        <button
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title="Download PDF"
        >
          <ArrowDownTrayIcon className="w-5 h-5" />
        </button>
        <button
          onClick={editMode ? handleCancelEdit : () => setEditMode(true)}
          className={`p-2 rounded-lg transition-colors ${
            editMode 
              ? 'text-red-500 hover:text-red-700 hover:bg-red-50' 
              : 'text-blue-500 hover:text-blue-700 hover:bg-blue-50'
          }`}
          title={editMode ? "Cancel Edit" : "Edit Order"}
        >
          {editMode ? <CancelIcon className="w-5 h-5" /> : <PencilIcon className="w-5 h-5" />}
        </button> */}
        <button
          onClick={onClose}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  );

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Progress Bar */}
{/*       <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Order Progress</h3>
          <span className="text-lg font-semibold text-blue-600">{progress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          Last updated: {formatDate(order.lastInteraction, 'datetime')}
        </p>
      </div> */}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
{/* Customer Information */}
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
  <div className="flex items-center mb-4">
    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mr-3">
      <UserIcon className="w-3 h-3 text-white" />
    </div>
    <h3 className="text-sm font-semibold text-gray-900">Customer Information</h3>
{/*     {customerNotifications.length > 0 && (
      <div className="ml-2 relative">
        <BellIcon className="w-5 h-5 text-blue-500" />
        <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
          {customerNotifications.length}
        </span>
      </div>
    )} */}
  </div>
  <div className="space-y-4">
    <div className='flex justify-between'>
{/*       <label className="block text-sm font-medium text-gray-700">Name</label>
 */} 
 <div>
      {editMode ? (
        <input
          type="text"
          value={editedOrder.customerName}
          onChange={(e) => setEditedOrder({
            ...editedOrder,
            customerName: e.target.value
          })}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        />
      ) : (
        <p className="mt-1 text-md font-medium text-gray-900">{order.customers[0].customer_name}</p>
      )}
      </div>
      <div>
     {/* Customer waiting time display - replace existing customer timer code */}
      {(() => {
        // Check if customer has responded (you can customize this logic based on your order status)
        const customerHasResponded = order.status && !['pending', 'awaiting_response'].includes(order.status.toLowerCase());
        
        if (customerHasResponded) {
          return (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 ml-2">
              <CheckCircleIcon className="w-3 h-3 mr-1" />
              Responded
            </span>
          );
        }
        
        if (customerWaitingTime) {
          if (customerWaitingTime.remainingSeconds > 0) {
            return (
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                getWaitingTimeColor(customerWaitingTime.remainingSeconds, customerWaitingTime.totalSeconds)
              }`}>
                <ClockIcon className="w-3 h-3 mr-1" />
                {customerWaitingTime.timeLeft} left
              </span>
            );
          } else {
            return (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                Response Overdue
              </span>
            );
          }
        }
        
        return null;
      })()}
    </div>
    </div>
{/*     <div className="flex items-center">
      <EnvelopeIcon className="w-4 h-4 text-gray-400 mr-2" />
      <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700">Email</label>
        {editMode ? (
          <input
            type="email"
            value={editedOrder.customerEmail}
            onChange={(e) => setEditedOrder({
              ...editedOrder,
              customerEmail: e.target.value
            })}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        ) : (
          <p className="mt-1 text-sm text-gray-900">{order.customers[0].email}</p>
        )}
      </div>
    </div> */}
        {renderCustomerPhone()}
                    <div className="mt-4 pt-3 border-t border-gray-200">

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

                                  <div>
                  <label className="block text-sm font-medium text-gray-700">Quote Amount</label>
                  <p className="mt-1 text-sm font-semibold text-green-600">
                      {formatCurrency(order.total)}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Response Status</label>
                  <span className="mt-1 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium">
                    { order.status ||'Not responded'}
                  </span>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Response Date</label>
                  <p className="mt-1 text-sm text-gray-900">
                     {order?.updated_at 
                        ? formatDate(order?.updated_at, 'datetime') 
                        : 'Not responded'}
                  </p>
                </div>
                </div>
                </div>

  
{/*     <div className="flex items-center">
      <MapPinIcon className="w-4 h-4 text-gray-400 mr-2" />
      <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700">Address</label>
        <p className="mt-1 text-sm text-gray-900"> {order.customers[0].city || order.customers[0].address || 'Not provided'}</p>
      </div>
    </div> */}
  </div>
  
  {/* Customer Notifications */}
{/*   <NotificationsList 
    notifications={customerNotifications} 
    title="Customer Notifications" 
  /> */}
</div>


{/* Supplier Information */}
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
  <div className="flex items-center mb-4">
    <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center mr-3">
      <BuildingOfficeIcon className="w-3 h-3 text-white" />
    </div>
    <h3 className="text-sm font-semibold text-gray-900">Supplier's Information</h3>
{/*     {totalSupplierNotifications > 0 && (
      <div className="ml-2 relative">
        <BellIcon className="w-5 h-5 text-purple-500" />
        <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
          {totalSupplierNotifications}
        </span>
      </div>
    )} */}
  </div>

  {/* Multiple Suppliers */}
{(() => {
  // Check if we have suppliers
  if (!order.suppliers || order.suppliers.length === 0) {
    return (
      <div className="text-center py-6">
        <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No Suppliers</h3>
        <p className="mt-1 text-sm text-gray-500">No supplier information available for this order.</p>
      </div>
    );
  }

  // Check if we have vendor responses
  if (!order.vendorResponses || order.vendorResponses.length === 0) {
    return (
      <div className="text-center py-6">
        <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No Vendor Responses</h3>
        <p className="mt-1 text-sm text-gray-500">Waiting for vendor responses for this order.</p>
      </div>
    );
  }

  // Check if any suppliers have matching vendor responses
  const matchedSuppliers = order.suppliers.filter(supplier => {
    const supplierId = supplier._id || supplier.vendor_id;
    return order.vendorResponses.some(response => {
      const responseVendorId = response.vendor_id?._id || response.vendor_id;
      return responseVendorId && responseVendorId.toString() === supplierId.toString();
    });
  });

  // If no matched suppliers, show no suppliers message
  if (matchedSuppliers.length === 0) {
    return (
      <div className="text-center py-6">
        <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No Matching Suppliers</h3>
        <p className="mt-1 text-sm text-gray-500">No suppliers match the vendor responses for this order.</p>
      </div>
    );
  }

  // Show matched suppliers
  return (
    <div className="space-y-6 max-h overflow-y-auto pr-2">
      {matchedSuppliers.map((supplier, index) => {
        const supplierId = supplier?._id || supplier.vendor_id;
        const supplierNotifications = supplierId ? getNotificationsForRecipient(supplierId) : [];
        
        return (
          <div key={index} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-md font-medium text-gray-900">
                {supplier?.name || supplier?.company_name || `Supplier ${index + 1}`}
              </h4>
              <div>
                {/* Timer display for suppliers */}
                {(() => {
                  const supplierId = supplier._id || supplier.vendor_id || supplier.id;
                  const timerData = supplierWaitingTimes[supplierId];
                  
                  // Check if supplier has responded
                const hasResponded = order?.vendorResponses?.some(response => {
                  const responseVendorId = response.vendor_id?._id || response.vendor_id?.id || response.vendor_id;
                  const supplierIdStr = supplierId.toString();
                  const responseIdStr = responseVendorId ? responseVendorId.toString() : null;
                  
                  const idsMatch = responseIdStr && responseIdStr === supplierIdStr;
                  const actuallyResponded = response.status && response.status.toLowerCase() !== 'respond now';
                  
                  return idsMatch && actuallyResponded;
                });

                  
                  if (hasResponded) {
                    return (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 ml-2">
                        <CheckCircleIcon className="w-3 h-3 mr-1" />
                        Responded
                      </span>
                    );
                  }
                  
                  if (timerData) {
                    if (timerData.remainingSeconds > 0) {
                      return (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ml-2 ${
                          getWaitingTimeColor(timerData.remainingSeconds, timerData.totalSeconds)
                        }`}>
                          <ClockIcon className="w-3 h-3 mr-1" />
                          {timerData.timeLeft} left
                        </span>
                      );
                    } else {
                      return (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 ml-2">
                          <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                          Time Expired
                        </span>
                      );
                    }
                  }
                  
                  return null;
                })()}
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Company Name</label>
                <p className="mt-1 text-sm text-gray-900">
                  {supplier?.company_name || 'Not provided'}
                </p>
              </div>
              {renderSupplierPhone(supplier, index)}
            </div>
            
            {/* Vendor Response Details */}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Quote Amount</label>
                  <p className="mt-1 text-sm font-semibold text-green-600">
                    {formatCurrency(calculateVendorResponseTotal(order.vendorResponses, supplier))}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Response Status</label>
                  <span className="mt-1 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium">
                    {order?.vendorResponses?.find(response => response.vendor_id._id === supplier._id)?.in_fulfillment_status?.toUpperCase() || 'Not responded'}
                  </span>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Response Date</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {order?.vendorResponses?.find(response => response.vendor_id._id === supplier._id)?.status_updated_at 
                      ? formatDate(order?.vendorResponses?.find(response => response.vendor_id._id === supplier._id)?.status_updated_at, 'datetime') 
                      : 'Not responded'}
                  </p>
                </div>
              </div>
              
              {supplier.notes && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <p className="mt-1 text-sm text-gray-600 bg-gray-50 p-2 rounded">
                    {supplier.notes}
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
})()}


  {/* Summary of all supplier notifications */}
{/*   {allSupplierNotifications.length > 0 && (
    <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-md">
      <div className="flex items-center mb-2">
        <BellIcon className="w-5 h-5 text-purple-500 mr-2" />
        <h4 className="text-sm font-medium text-purple-800">All Supplier Notifications Summary</h4>
        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          {totalSupplierNotifications} total
        </span>
      </div>
      <div className="space-y-2">
        {allSupplierNotifications.map((supplier, index) => (
          <div key={index} className="text-sm">
            <span className="font-medium text-purple-900">{supplier.supplierName}:</span>
            <span className="text-purple-700 ml-1">{supplier.notifications.length} notifications</span>
          </div>
        ))}
      </div>
    </div>
  )} */}
</div>

      </div>

      {/* Order Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center">
            <CalendarDaysIcon className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Order Date</p>
            <p className="font-semibold text-gray-900">{formatDate(order.orderDate)}</p>
          </div>
          <div className="text-center">
            <CurrencyDollarIcon className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Total Amount</p>
            <p className="font-semibold text-blue-600">{formatCurrency(order.total)}</p>
          </div>
          <div className="text-center">
            <ShoppingCartIcon className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Items</p>
            <p className="font-semibold text-gray-900">{order.items?.length || 0}</p>
          </div>
          <div className="text-center">
            <ClockIcon className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Expected Delivery</p>
            <p className="font-semibold text-gray-900">
              {order.expectedDelivery ? formatDate(order.expectedDelivery) : 'TBD'}
            </p>
          </div>
        </div>
      </div>

      {/* Status Update Section */}
      {editMode && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Update Order Status</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={editedOrder.status}
                onChange={(e) => setEditedOrder({
                  ...editedOrder,
                  status: e.target.value
                })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="quotation_sent">Quotation Sent</option>
                <option value="awaiting_payment">Awaiting Payment</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="completed">Completed</option>
                <option value="on_hold">On Hold</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
              <select
                value={editedOrder.priority}
                onChange={(e) => setEditedOrder({
                  ...editedOrder,
                  priority: e.target.value
                })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderItemsTab = () => (
    <div className="space-y-6">
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
  <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Items</h3>
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Item
          </th>
          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
            Quantity
          </th>
          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
            Unit Price
          </th>
          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
            GST
          </th>
          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
            Total
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {order.items?.map((item, index) => (
          <tr key={index} className="hover:bg-gray-50">
            <td className="px-6 py-4 whitespace-nowrap">
              <div>
                <p className="text-sm font-medium text-gray-900">{item.item_name}</p>
                {item.description && (
                  <p className="text-sm text-gray-500">{item.description}</p>
                )}
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
              {item.quantity}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
              {formatCurrency(item.selling_price)}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
              {formatCurrency(item.total_tax_amount)}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
              {formatCurrency(item.total)}
            </td>
          </tr>
        ))}
        
        {/* Subtotal Row */}
        <tr className="bg-gray-50 border-t-2 border-gray-200">
          <td colSpan="4" className="px-6 py-3 text-right text-sm font-semibold text-gray-700">
            Subtotal
          </td>
          <td className="px-6 py-3 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
            {formatCurrency(order.subtotal)}
          </td>
        </tr>
        
        {/* Tax Amount Row */}
        <tr className="bg-gray-50">
          <td colSpan="4" className="px-6 py-3 text-right text-sm font-semibold text-gray-700">
            Tax Amount
          </td>
          <td className="px-6 py-3 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
            {formatCurrency(order.tax_amount)}
          </td>
        </tr>
        
        {/* Total Row */}
        <tr className="bg-blue-50 border-t-2 border-blue-200">
          <td colSpan="4" className="px-6 py-4 text-right text-lg font-bold text-gray-900">
            Total
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-right text-lg font-bold text-blue-600">
            {formatCurrency(order.total)}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</div>


      {/* Additional Order Details */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Special Instructions</label>
            <p className="mt-1 text-sm text-gray-900">{order.specialInstructions || 'None'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Delivery Notes</label>
            <p className="mt-1 text-sm text-gray-900">{order.deliveryNotes || 'None'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Payment Terms</label>
            <p className="mt-1 text-sm text-gray-900">{order.paymentTerms || 'Standard'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Shipping Method</label>
            <p className="mt-1 text-sm text-gray-900">{order.shippingMethod || 'Standard Shipping'}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const TimelineDot = ({ type, children }) => {
    const getColorClass = () => {
      switch (type) {
        case 'status': return 'bg-blue-500';
        case 'email': return 'bg-purple-500';
        case 'phone': return 'bg-green-500';
        case 'chat': return 'bg-yellow-500';
        default: return 'bg-gray-500';
      }
    };

    return (
      <div className={`absolute left-0 top-2 w-8 h-8 ${getColorClass()} rounded-full flex items-center justify-center text-white z-10`}>
        {children}
      </div>
    );
  };

  const renderTimelineTab = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Order Timeline</h3>
      <div className="relative pl-10">
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
        <div className="space-y-6">
          {timeline.map((event, index) => (
            <div key={index} className="relative">
              <TimelineDot type={event.type}>
                {event.type === 'status' && <CheckCircleIcon className="w-4 h-4" />}
                {event.type === 'email' && <EnvelopeIcon className="w-4 h-4" />}
                {event.type === 'phone' && <PhoneIcon className="w-4 h-4" />}
                {event.type === 'chat' && <ChatBubbleLeftRightIcon className="w-4 h-4" />}
                {!['status', 'email', 'phone', 'chat'].includes(event.type) && 
                  <EllipsisHorizontalCircleIcon className="w-4 h-4" />}
              </TimelineDot>
              <div className="pl-10">
                <h4 className="text-sm font-semibold text-gray-900">{event.title}</h4>
                <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatDate(event.timestamp, 'datetime')} • {event.user}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

/*   const renderCommunicationsTab = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Communications</h3>
        <button
          onClick={() => setEmailDialog(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <EnvelopeIcon className="w-4 h-4 mr-2" />
          Send Email
        </button>
      </div>
      
      {order.communications && order.communications.length > 0 ? (
        <div className="space-y-4">
          {order.communications.map((comm, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  comm.type === 'email' ? 'bg-blue-100 text-blue-600' :
                  comm.type === 'phone' ? 'bg-green-100 text-green-600' :
                  'bg-purple-100 text-purple-600'
                }`}>
                  {comm.type === 'email' && <EnvelopeIcon className="w-5 h-5" />}
                  {comm.type === 'phone' && <PhoneIcon className="w-5 h-5" />}
                  {comm.type === 'chat' && <ChatBubbleLeftRightIcon className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center">
                    <h4 className="text-sm font-semibold text-gray-900">
                      {comm.subject || `${comm.type.toUpperCase()} Communication`}
                    </h4>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {comm.type.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">{comm.message}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    {formatDate(comm.timestamp, 'datetime')} • {comm.user}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <ChatBubbleLeftRightIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-semibold text-gray-900 mb-2">No Communications Yet</h4>
          <p className="text-sm text-gray-500 mb-4">Start a conversation with the customer</p>
          <button
            onClick={() => setEmailDialog(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <EnvelopeIcon className="w-4 h-4 mr-2" />
            Send First Email
          </button>
        </div>
      )}
    </div>
  ); */

  const renderEmailDialog = () => (
    emailDialog && (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => {setEmailDialog(false);
            setCurrentRecipient(null);
          }}></div>
          
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
          
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Send Email
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
                      <input
                        type="email"
                        value={emailData.to}
                        onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                      <input
                        type="text"
                        value={emailData.subject}
                        onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                      <textarea
                        rows={6}
                        value={emailData.message}
                        onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
                        placeholder="Type your message here..."
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
              type="button"
              onClick={handleSendEmail}
              disabled={!emailData.to || !emailData.subject || !emailData.message || emailLoading}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {emailLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </div>
              ) : (
                'Send Email'
              )}
            </button>
              <button
                type="button"
                onClick={() => {
                setEmailDialog(false);
                setCurrentRecipient(null);
              }}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  );

  const tabs = [
    { id: 0, name: 'Overview' },
    { id: 1, name: 'Items' },
    { id: 2, name: 'Timeline' },
    //{ id: 3, name: 'Communications' }
  ];

  return (
    <>
      {/* Modal Backdrop */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
          
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
          
          {/* Modal Content */}
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full max-h-[90vh] overflow-y-auto">
            {renderHeader()}
            
            <div className="px-6 py-4">
              {/* Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.name}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="min-h-[400px]">
                {activeTab === 0 && renderOverviewTab()}
                {activeTab === 1 && renderItemsTab()}
                {activeTab === 2 && renderTimelineTab()}
{/*                 {activeTab === 3 && renderCommunicationsTab()}
 */}              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-between items-center">
              <div>
                {editMode && (
                  <button
                    onClick={handleSaveEdit}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <CheckIcon className="w-4 h-4 mr-2" />
                    Save Changes
                  </button>
                )}
              </div>
              <div>
                <button
                  onClick={onClose}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {renderEmailDialog()}
<SMSDialog 
      smsDialog={smsDialog}
      setSmsDialog={setSmsDialog}
      smsData={smsData}
      setSmsData={setSmsData}
      handleSendSMS={handleSendSMS}
      currentRecipient ={currentRecipient}
    />
<WhatsAppDialog 
      whatsappDialog={whatsappDialog}
      setWhatsappDialog={setWhatsappDialog}
      whatsappData={whatsappData}
      setWhatsappData={setWhatsappData}
      handleSendWhatsApp={handleSendWhatsApp}
      currentRecipient ={currentRecipient}
    /> 
       </>
  );
};

export default OrderDetailsModal;

